import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';
import styled from 'styled-components';
import { Color, Grid, Type } from '../../../app/StyleGuide';
import { FormFieldContainer, Input, Label } from '../../../components/FormElements';
import { Supply } from '../models/timeline';

type SupplyWithId = Supply & { sortId?: number };

type SortableSupplyProps = {
  supply: SupplyWithId;
  index: number;
  onSupplyChange: (index: number, field: 'name' | 'quantity', value: string) => void;
  onDeleteSupply: (index: number) => void;
};

const SortableSupplyItem = styled.div<{ isDragging: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  background: ${props => (props.isDragging ? '#f0f0f0' : 'transparent')};
  border-radius: 4px;
  cursor: grab;
  padding-bottom: ${Grid._4};

  &:active {
    cursor: grabbing;
  }
`;

const SortableSupplyContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #666;
  cursor: grab;
  margin-top: ${Grid._5};

  &:active {
    cursor: grabbing;
  }
`;

const SupplyInput = styled(Input)`
  width: 100% !important;
`;

const SupplyInputContainer = styled(FormFieldContainer)`
  flex: 1;
  margin-bottom: 0;
`;

const DeleteButton = styled.div`
  font-size: ${Type.Scale._3};
  color: ${Color.Red._50};
  cursor: pointer;
  padding: ${Grid._2};
  margin-top: ${Grid._5};

  &:hover {
    color: ${Color.Red._60};
  }
`;

export function SortableSupply({ supply, index, onSupplyChange, onDeleteSupply }: SortableSupplyProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <SortableSupplyItem ref={setNodeRef} style={style} isDragging={isDragging}>
      <SortableSupplyContainer>
        <DragHandle {...attributes} {...listeners}>
          <i className="fas fa-grip-vertical"></i>
        </DragHandle>

        <SupplyInputContainer>
          <Label htmlFor={`supply-name-${index}`}>Supply name</Label>
          <SupplyInput
            id={`supply-name-${index}`}
            name="name"
            value={supply.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSupplyChange(index, 'name', e.target.value)}
            placeholder="Supply name"
            aria-label="Supply name"
          />
        </SupplyInputContainer>

        <SupplyInputContainer>
          <Label htmlFor={`supply-quantity-${index}`}>Qty</Label>
          <SupplyInput
            id={`supply-quantity-${index}`}
            name="quantity"
            value={supply.quantity}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSupplyChange(index, 'quantity', e.target.value)}
            placeholder="Qty"
            aria-label="Quantity"
          />
        </SupplyInputContainer>

        <DeleteButton onClick={() => onDeleteSupply(index)} aria-label="Delete supply">
          <i className="fas fa-times" />
        </DeleteButton>
      </SortableSupplyContainer>
      {supply.downloadable && supply.downloadable.fileName && (
        <p>
          <i className="fas fa-download" style={{ marginRight: Grid._2 }} />
          <span>Includes downloadable file</span>
        </p>
      )}
    </SortableSupplyItem>
  );
}
